import React from "react";
import {
  DownloadIcon,
  UserPlusIcon,
  UsersIcon,
  FileTextIcon,
  ClockIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Post, Friend } from "../../lib/supabase";

interface PostItemProps {
  post: Post;
  currentUserId?: string;
  currentFriends?: Friend[];
  sentRequests?: Friend[];
  pendingRequests?: Friend[];
  onAddFriend?: (userId: string) => void;
  onAcceptFriendRequest?: (requestId: string, senderId: string) => void;
  onInviteCollaborator?: (userId: string) => void;
}

export const PostItem: React.FC<PostItemProps> = ({
  post,
  currentUserId,
  currentFriends,
  sentRequests,
  pendingRequests,
  onAddFriend,
  onAcceptFriendRequest,
  onInviteCollaborator,
}) => {
  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (post.user_profiles?.avatar_url) {
      return post.user_profiles.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Baru saja";
    if (diffInMinutes < 60) return `${diffInMinutes} menit lalu`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} jam lalu`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} hari lalu`;

    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get file type icon
  const getFileIcon = (fileType?: string) => {
    if (!fileType) return FileTextIcon;

    if (fileType.includes("pdf")) return FileTextIcon;
    if (fileType.includes("word") || fileType.includes("document"))
      return FileTextIcon;
    if (fileType.includes("powerpoint") || fileType.includes("presentation"))
      return FileTextIcon;

    return FileTextIcon;
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";

    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;

    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  };

  // Handle file download
  const handleDownload = (url: string, fileName?: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Determine button state and content
  const isSelfPost = currentUserId === post.user_id;
  const isFriend =
    currentFriends?.some(f => f.friend_id === post.user_id) || false;
  const hasSentRequest =
    sentRequests?.some(req => req.friend_id === post.user_id) || false;
  const hasReceivedRequest =
    pendingRequests?.some(req => req.user_id === post.user_id) || false;

  // Find the received request for accept functionality
  const receivedRequest = pendingRequests?.find(
    req => req.user_id === post.user_id
  );

  const FileIcon = getFileIcon(post.file_type);

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm relative">
      {/* Action Buttons - Top Right */}
      {!isSelfPost && (
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {isFriend ? (
            // Show Collaborate button for friends
            <Button
              onClick={() => onInviteCollaborator?.(post.user_id)}
              size="sm"
              variant="outline"
              className="text-[#0217ff] border-[#0217ff] hover:bg-[#0217ff] hover:text-white transition-all duration-300 px-3 text-xs"
            >
              <UsersIcon className="w-4 h-4 mr-1" />
              Kolaborasi
            </Button>
          ) : hasReceivedRequest && receivedRequest ? (
            // Show Accept Request button for received friend requests
            <Button
              onClick={() =>
                onAcceptFriendRequest?.(
                  receivedRequest.id,
                  receivedRequest.user_id
                )
              }
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300 px-3 text-xs"
            >
              <UserPlusIcon className="w-4 h-4 mr-1" />
              Terima Permintaan
            </Button>
          ) : hasSentRequest ? (
            // Show Pending button for sent friend requests
            <Button
              size="sm"
              variant="outline"
              disabled
              className="text-yellow-600 border-yellow-600 cursor-not-allowed px-3 text-xs"
            >
              <ClockIcon className="w-4 h-4 mr-1" />
              Menunggu
            </Button>
          ) : (
            // Show Add Friend button for non-friends with no pending requests
            <Button
              onClick={() => onAddFriend?.(post.user_id)}
              size="sm"
              variant="outline"
              className="text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white transition-all duration-300 px-3 text-xs"
            >
              <UserPlusIcon className="w-4 h-4 mr-1" />
              Tambah Teman
            </Button>
          )}
        </div>
      )}

      {/* User Info */}
      <div className="flex items-center gap-3 mb-4 pr-12">
        <Avatar className="w-10 h-10 flex-shrink-0">
          <AvatarFallback>
            <img
              className="w-full h-full object-cover"
              alt="User Avatar"
              src={getAvatarUrl()}
            />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#373e46] text-sm sm:text-base truncate">
            {post.user_profiles?.name || "Unknown User"}
          </h4>
          <p className="text-xs sm:text-sm text-gray-500">
            {formatTimestamp(post.created_at)}
          </p>
        </div>
      </div>

      {/* Post Content */}
      {post.content && (
        <div className="mb-4">
          <p className="text-[#373e46] text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
            {post.content}
          </p>
        </div>
      )}

      {/* Media Preview */}
      {post.media_url && (
        <div className="mb-4">
          {post.media_type === "image" ? (
            <div className="relative rounded-lg overflow-hidden">
              <img
                src={post.media_url}
                alt="Post media"
                className="w-full max-h-96 object-cover"
              />
            </div>
          ) : post.media_type === "video" ? (
            <div className="relative rounded-lg overflow-hidden">
              <video
                src={post.media_url}
                controls
                className="w-full max-h-96 object-cover"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : null}
        </div>
      )}

      {/* File Preview */}
      {post.file_url && (
        <div className="mb-4">
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileIcon className="w-5 h-5 text-blue-600" />
              </div>

              <div className="flex-1 min-w-0">
                <h5
                  className="font-medium text-[#373e46] text-sm truncate"
                  title={post.file_name || "Document"}
                >
                  {post.file_name || "Document"}
                </h5>
                <p className="text-xs text-gray-500">
                  {formatFileSize(post.file_size)}
                </p>
              </div>

              <Button
                onClick={() => handleDownload(post.file_url!, post.file_name)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-100 h-8 px-3 text-xs flex-shrink-0"
              >
                <DownloadIcon className="w-3 h-3" />
                <span className="hidden sm:inline">Download</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
